"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { getSession, signInWithGoogle, signOut } from "@/lib/auth";
import {
  clearAccountSession,
  getAccountSessionSnapshot,
  storeAccountSession,
  type AccountSessionSource,
} from "@/lib/account-session";
import { getLastReadingSnapshot, subscribeLastReading } from "@/lib/last-reading";
import { reportJsError } from "@/lib/js-error-beacon";
import {
  accountSessionFromAuth,
  fetchAccountOrders,
  type AccountOrdersResponse,
} from "./account-api";
import { AccountError, AccountReady, AccountSent, LoginForm } from "./AccountPanels";

type Stage = "loading" | "login" | "sent" | "ready" | "error";

function tokenFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const token = url.searchParams.get("token")?.trim();
  if (!token) return null;
  storeAccountSession(token, "contact");
  url.searchParams.delete("token");
  window.history.replaceState({}, "", url.toString());
  return token;
}

function reportAccountBootstrapFailure(boundary: string): void {
  reportJsError({
    error_message: "Private account session bootstrap failed",
    error_name: "account_session_bootstrap",
    boundary,
  });
}

export default function AccountClient() {
  const [stage, setStage] = useState<Stage>("loading");
  const [data, setData] = useState<AccountOrdersResponse | null>(null);
  const [token, setToken] = useState("");
  const [sessionSource, setSessionSource] = useState<AccountSessionSource | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [loginNotice, setLoginNotice] = useState("");
  const [sentChannel, setSentChannel] = useState<"email" | "phone">("phone");
  const rememberedReading = useSyncExternalStore(
    subscribeLastReading,
    getLastReadingSnapshot,
    () => null
  );

  useEffect(() => {
    let cancelled = false;

    async function openAccount(accountToken: string, source: AccountSessionSource) {
      const orders = await fetchAccountOrders(accountToken);
      if (cancelled) return false;
      if (orders === "unauthorized") {
        clearAccountSession();
        return false;
      }
      setToken(accountToken);
      setSessionSource(source);
      setData(orders);
      setStage("ready");
      return true;
    }

    async function bootstrap() {
      try {
        const urlToken = tokenFromUrl();
        const stored = getAccountSessionSnapshot();
        if (urlToken && (await openAccount(urlToken, "contact"))) return;
        if (!urlToken && stored && (await openAccount(stored.token, stored.source))) return;
      } catch (error) {
        if (cancelled) return;
        setErrorMsg(error instanceof Error ? error.message : "Network error loading account");
        setStage("error");
        return;
      }

      try {
        const authSession = await getSession();
        if (cancelled) return;
        if (!authSession) {
          setStage("login");
          return;
        }
        const exchanged = await accountSessionFromAuth(authSession.access_token);
        if (exchanged === "unauthorized") {
          setLoginNotice("Your Google session expired. Sign in again to reopen My Jyotishi.");
          setStage("login");
          return;
        }
        storeAccountSession(exchanged, "auth");
        if (!(await openAccount(exchanged, "auth"))) setStage("login");
      } catch {
        if (cancelled) return;
        reportAccountBootstrapFailure("AccountClient.authExchange");
        setLoginNotice(
          "Google sign-in could not open your private account. Try again or use a private link."
        );
        setStage("login");
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  async function resetAccount() {
    clearAccountSession();
    if (sessionSource === "auth" || data?.contact_kind === "auth") {
      try {
        await signOut();
      } catch {
        reportAccountBootstrapFailure("AccountClient.signOut");
      }
    }
    setData(null);
    setToken("");
    setSessionSource(null);
    setStage("login");
  }

  if (stage === "loading") return <AccountSent title="Loading My Jyotishi" body="Please wait." />;
  if (stage === "login") {
    return (
      <LoginForm
        notice={loginNotice}
        rememberedReading={rememberedReading}
        onGoogle={() => signInWithGoogle("/account/")}
        onSubmitted={(channel) => {
          setSentChannel(channel);
          setStage("sent");
        }}
      />
    );
  }
  if (stage === "sent") {
    return (
      <AccountSent
        title={sentChannel === "phone" ? "Check WhatsApp" : "Check your email"}
        body={
          sentChannel === "phone"
            ? "If an Ishvaram report exists for that number, its private link will arrive on WhatsApp. Open it, then choose View all orders."
            : "If an Ishvaram order exists for that address, the private login link will arrive shortly."
        }
        onReset={() => void resetAccount()}
      />
    );
  }
  if (stage === "error") {
    return <AccountError message={errorMsg} onReset={() => void resetAccount()} />;
  }
  return (
    <AccountReady
      data={data}
      token={token}
      rememberedReading={rememberedReading}
      onDataChange={setData}
      onReset={() => void resetAccount()}
    />
  );
}
